import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import MemberProfileHover from "@/components/MemberProfileHover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, ArrowRightLeft, Settings, Sparkles } from "lucide-react";
import { getFullName } from "@/lib/userUtils";
import { cn } from "@/lib/utils";
import { toastSuccess, toastError } from "@/lib/toast";
import {
  toastEntitlementOrError,
  getEntitlementCode,
  entitlementMessageFor,
} from "@/lib/entitlementToast";
import { messageForCatchupGenerateError } from "@/lib/catchupGenerateError";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import PostCard from "./PostCard";
import PostComposer from "./PostComposer";
import RichMarkdownEditor, {
  isMarkdownEmpty,
  normalizeEditorMarkdown,
} from "./RichMarkdownEditor";
import KanbanBoard from "./KanbanBoard";
import KanbanLaneManager from "./KanbanLaneManager";
import SprintScrubber, { findCurrentSprintIndex } from "./SprintScrubber";
import SprintCadenceFields, {
  cadenceFormValues,
  isAllowedFirstSprintStart,
  latestAllowedFirstSprintStart,
} from "./SprintCadenceFields";
import TeamRoadmap from "./TeamRoadmap";
import HandoffDialog from "./HandoffDialog";
import InboundHandoffsPanel from "./InboundHandoffsPanel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import InviteUserDialog from "./InviteUserDialog";
import CatchUpCard, { CatchUpBriefing } from "./CatchUpCard";
import TeamChannelsPanel from "./TeamChannelsPanel";
import TeamDefaultChannel from "./TeamDefaultChannel";
import TeamStuckStrip from "./TeamStuckStrip";
import TeamFilesPanel from "./TeamFilesPanel";
import {
  permalinkForPost,
  withReturnQuery,
  pathWithPostAnchor,
  feedPostAnchorId,
  kanbanCardAnchorId,
  safeReturnPath,
} from "@/lib/permalinks";
import { useScrollToFeedPostAnchor } from "@/lib/useScrollToFeedPostAnchor";
import { prependCreatedPost, mergePinnedFeed } from "@/lib/feedPosts";
import { uploadPostAttachments } from "@/lib/postAttachments";
import MediaAttachBar from "@/components/MediaAttachBar";
import CardEditDialog, {
  CardAssigneesPicker,
  dateInputToIso,
} from "@/components/CardEditDialog";

import { API_BASE_URL } from "@/lib/apiBase";
import {
  FIBONACCI_POINTS,
  workKindOf,
  postIdOfWork,
  isCardOverdue,
} from "@/lib/workKind";
import { listTeamChannels } from "@/lib/dmApi";

const TEAM_ORGANIZATION_TOOLTIP =
  "Organization-wide teams are visible to all organization members. Members can join and follow.";
const TEAM_RESTRICTED_TOOLTIP =
  "Restricted teams require an invitation to join (or a request-to-join when searchable). They are not followable.";

/** Map legacy tab=posts → feed; settings lives on its own page; default feed. */
const normalizeTeamTab = (tab) => {
  if (!tab || tab === "posts" || tab === "settings") return "feed";
  return tab;
};

const TeamDetail = () => {
  const { teamId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = searchParams.get("org_id");
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useAuth();
  const confirm = useConfirm();
  const [currentUser, setCurrentUser] = useState(null);
  
  const [team, setTeam] = useState(null);
  const [posts, setPosts] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [handoffStubs, setHandoffStubs] = useState([]);
  const [members, setMembers] = useState([]);
  const [showHandoffDialog, setShowHandoffDialog] = useState(false);
  const [showInboundHandoffs, setShowInboundHandoffs] = useState(false);
  const [inboundHandoffs, setInboundHandoffs] = useState([]);
  const [defaultChannel, setDefaultChannel] = useState(null);
  const [isCancellingHandoff, setIsCancellingHandoff] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initialize activeTab from URL; legacy "posts" maps to "feed"
  const [activeTab, setActiveTab] = useState(() => {
    return normalizeTeamTab(searchParams.get("tab"));
  });
  const [interactionCounts, setInteractionCounts] = useState({});
  const [likedPosts, setLikedPosts] = useState(new Set()); // Track which posts current user has liked
  const [userMembershipStatus, setUserMembershipStatus] = useState(null); // 'member', 'following', 'none'
  const [isJoining, setIsJoining] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isUnfollowing, setIsUnfollowing] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showUnfollowDialog, setShowUnfollowDialog] = useState(false);
  const [showRemoveMemberDialog, setShowRemoveMemberDialog] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] = useState(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [showCreateCardDialog, setShowCreateCardDialog] = useState(false);
  const [showCardDetailDialog, setShowCardDetailDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardFormData, setCardFormData] = useState({
    title: "",
    content: "",
    kanban_status: "Backlog", // Changed from "To Do" - this is just initial state, will be overridden in handleCreateCard
    assignee_ids: [],
    starts_at: "",
    due_date: "",
    priority: "medium",
    sprint_id: "",
    work_kind: "task",
    parent_id: "",
    story_points: "",
  });
  const [composerFormKey, setComposerFormKey] = useState(0);
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const [isCreatingCard, setIsCreatingCard] = useState(false);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [kanbanLanes, setKanbanLanes] = useState([]);
  const [, setLoadingLanes] = useState(false);
  const [showLaneManager, setShowLaneManager] = useState(false);
  const [highlightedCardId, setHighlightedCardId] = useState(null);
  const [inspectTarget, setInspectTarget] = useState(null);
  const inspectTargetRef = useRef(inspectTarget);
  inspectTargetRef.current = inspectTarget;
  const [sprints, setSprints] = useState([]);
  const [sprintIndex, setSprintIndex] = useState(0);
  const [sprintFilterMode, setSprintFilterMode] = useState("all");
  const [startingSprint, setStartingSprint] = useState(false);
  const [showSprintCadenceDialog, setShowSprintCadenceDialog] = useState(false);
  const [sprintCadenceDraft, setSprintCadenceDraft] = useState(() =>
    cadenceFormValues(null)
  );
  const [roadmapPosts, setRoadmapPosts] = useState([]);
  const [roadmapStubs, setRoadmapStubs] = useState([]);
  const [showRenameSprintDialog, setShowRenameSprintDialog] = useState(false);
  const [cardPendingFiles, setCardPendingFiles] = useState([]);
  const cardDeepLinkHandledRef = useRef(null);
  const [renameSprintName, setRenameSprintName] = useState("");
  const [renamingSprint, setRenamingSprint] = useState(false);
  const [orgName, setOrgName] = useState(null);
  const [orgOwnedBy, setOrgOwnedBy] = useState(null);
  const [canOrgPostCritical, setCanOrgPostCritical] = useState(false);
  const [canManageOrgSettings, setCanManageOrgSettings] = useState(false);
  const [canIntegrations, setCanIntegrations] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [memberPermissionsTarget, setMemberPermissionsTarget] = useState(null);
  const [memberPermissionsForm, setMemberPermissionsForm] = useState({
    role: "member",
    overrides: [],
  });
  const [isSavingMemberPermissions, setIsSavingMemberPermissions] = useState(false);
  const [orgMembers, setOrgMembers] = useState([]);
  const [allowNonMemberIntake, setAllowNonMemberIntake] = useState(false);
  const [boardBriefing, setBoardBriefing] = useState(null);
  const [boardSummarizeMeta, setBoardSummarizeMeta] = useState(null);
  const [boardSummarizeError, setBoardSummarizeError] = useState(null);
  const [boardSummarizing, setBoardSummarizing] = useState(false);

  axios.defaults.withCredentials = true;

  // Interceptors handle token refresh; this just runs the request with the current token
  const makeAuthenticatedRequest = async (requestFn, token = accessToken) => {
    return requestFn(token);
  };

  // Fetch team details from lists the user can see, including restricted
  // membership teams (organization/requestable lists omit non-searchable ones).
  const fetchTeamDetails = async (token) => {
    if (!orgId) {
      throw new Error("Organization ID (org_id) is required");
    }

    const targetId = parseInt(teamId, 10);
    const matchTeam = (list) =>
      (list || []).find((t) => (t.team_id ?? t.teamId) === targetId);

    try {
      const [organizationResponse, requestableResponse, membershipsResponse, followsResponse] =
        await Promise.all([
          axios.get(`${API_BASE_URL}/team/organization`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { org_id: orgId },
          }),
          axios.get(`${API_BASE_URL}/team/requestable`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { org_id: orgId },
          }),
          axios.get(`${API_BASE_URL}/team/memberships`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { org_id: orgId },
          }),
          axios.get(`${API_BASE_URL}/team/follows`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { org_id: orgId },
          }),
        ]);

      const foundTeam =
        matchTeam(organizationResponse.data) ||
        matchTeam(requestableResponse.data) ||
        matchTeam(membershipsResponse.data) ||
        matchTeam(followsResponse.data);

      if (!foundTeam) {
        throw new Error("Team not found");
      }

      return foundTeam;
    } catch (err) {
      console.error("Failed to fetch team:", err);
      throw err;
    }
  };

  // Fetch team posts (board) — API returns { posts, stubs } (legacy bare array still supported)
  const fetchTeamPosts = async (token, opts = {}) => {
    const params = {
      org_id: orgId,
      limit: 100,
      offset: 0,
    };
    if (opts.sprintId) {
      params.sprint_id = opts.sprintId;
    }
    const response = await axios.get(`${API_BASE_URL}/posts/team/${teamId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params,
    });
    return response.data;
  };

  const fetchSprints = async (token) => {
    const response = await axios.get(
      `${API_BASE_URL}/team/${teamId}/sprints`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { org_id: orgId },
      }
    );
    return Array.isArray(response.data) ? response.data : [];
  };

  // Ranked team Feed (silent lanes excluded)
  const fetchTeamFeed = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/posts/team/${teamId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        org_id: orgId,
        limit: 50,
        offset: 0,
        ranked: true,
      },
    });
    return response.data;
  };

  const applyTeamPostsResponse = (data) => {
    if (Array.isArray(data)) {
      setPosts(data);
      setHandoffStubs([]);
      return data;
    }
    const nextPosts = data?.posts || [];
    setPosts(nextPosts);
    setHandoffStubs(data?.stubs || []);
    return nextPosts;
  };

  const applyTeamFeedResponse = (data) => {
    if (Array.isArray(data)) {
      setFeedPosts(data);
      return data;
    }
    const nextFeed = mergePinnedFeed(data?.pinned, data?.posts);
    setFeedPosts(nextFeed);
    return nextFeed;
  };

  const fetchDefaultChannel = async (token) => {
    const list = await listTeamChannels(token, orgId, teamId);
    return (
      (Array.isArray(list) ? list : []).find((ch) => ch.is_default) || null
    );
  };

  const fetchInboundHandoffs = async (token) => {
    const response = await axios.get(
      `${API_BASE_URL}/posts/handoffs/inbound`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          team_id: teamId,
          org_id: orgId,
          status: "pending",
        },
      }
    );
    return Array.isArray(response.data) ? response.data : [];
  };

  const refreshTeamHomeExtras = async () => {
    if (!accessToken || !orgId || !teamId) return;
    try {
      const [channel, handoffs] = await Promise.all([
        makeAuthenticatedRequest(fetchDefaultChannel),
        makeAuthenticatedRequest(fetchInboundHandoffs).catch(() => []),
      ]);
      setDefaultChannel(channel);
      setInboundHandoffs(handoffs);
    } catch (err) {
      console.error("Failed to load team home extras:", err);
    }
  };

  const refreshTeamPosts = async (override = {}) => {
    const mode = override.sprintFilterMode ?? sprintFilterMode;
    const idx = override.sprintIndex ?? sprintIndex;
    const list = override.sprints ?? sprints;
    const selected = list[idx];
    const sprintId =
      mode === "sprint" && selected
        ? selected.sprint_id ?? selected.sprintId
        : null;
    const [postsData, feedData] = await Promise.all([
      makeAuthenticatedRequest((token) =>
        fetchTeamPosts(token, { sprintId })
      ),
      makeAuthenticatedRequest(fetchTeamFeed),
    ]);
    applyTeamFeedResponse(feedData);
    return applyTeamPostsResponse(postsData);
  };

  const refreshSprints = async () => {
    try {
      const list = await makeAuthenticatedRequest(fetchSprints);
      setSprints(list);
      return list;
    } catch (err) {
      console.error("Failed to load sprints:", err);
      setSprints([]);
      return [];
    }
  };

  const loadRoadmapData = async () => {
    try {
      const data = await makeAuthenticatedRequest((token) =>
        fetchTeamPosts(token, {})
      );
      if (Array.isArray(data)) {
        setRoadmapPosts(data);
        setRoadmapStubs([]);
      } else {
        setRoadmapPosts(data?.posts || []);
        setRoadmapStubs(data?.stubs || []);
      }
    } catch (err) {
      console.error("Failed to load roadmap posts:", err);
      setRoadmapPosts([]);
      setRoadmapStubs([]);
    }
  };

  // Fetch kanban lanes for team
  const fetchKanbanLanes = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/team/${teamId}/kanban-lanes`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        org_id: orgId,
        team_id: parseInt(teamId),
      },
    });
    return response.data;
  };

  // Fetch current user
  const fetchCurrentUser = async (token) => {
    const response = await axios.get(`${API_BASE_URL}/user/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  // Fetch organization details
  const fetchOrgDetails = async (token) => {
    if (!orgId) return null;
    const response = await axios.get(`${API_BASE_URL}/org/${orgId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  // Fetch team members
  const fetchTeamMembers = async (token) => {
    if (!orgId) return [];
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/team/${teamId}/members`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { org_id: orgId },
        }
      );
      
      // Store full response including user, role, and title
      return response.data; // Returns Array<{ user: User; role: string; title?: string }>
    } catch (err) {
      console.error("Failed to fetch team members:", err);
      return [];
    }
  };

  // Fetch org members for title lookup in posts
  const fetchOrgMembers = async () => {
    if (!accessToken || !orgId) return;

    try {
      const response = await makeAuthenticatedRequest(async (token) => {
        return await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      });

      setOrgMembers(response.data || []);
    } catch (err) {
      console.error("Failed to fetch org members:", err);
    }
  };

  // Check user's membership status
  const checkUserMembershipStatus = async (token) => {
    if (!orgId) return 'none';

    try {
      // Check if user is a member
      const membershipsResponse = await axios.get(`${API_BASE_URL}/team/memberships`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { org_id: orgId },
      });
      
      const isMember = membershipsResponse.data.some(t => t.team_id === parseInt(teamId));
      if (isMember) return 'member';

      // Check if user is following
      const followsResponse = await axios.get(`${API_BASE_URL}/team/follows`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { org_id: orgId },
      });
      
      const isFollowing = followsResponse.data.some(t => t.team_id === parseInt(teamId));
      if (isFollowing) return 'following';

      return 'none';
    } catch (err) {
      console.error("Failed to check membership status:", err);
      return 'none';
    }
  };

  // Fetch post interactions
  const fetchPostInteractions = async (postId, token) => {
    const response = await axios.get(
      `${API_BASE_URL}/posts/${postId}/interactions`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: orgId ? { org_id: orgId } : {},
      }
    );
    return response.data;
  };

  // Calculate interaction counts for posts
  // Accepts optional userOverride parameter to avoid closure issues with currentUser state
  // Returns counts and isLiked status to allow batching of likedPosts updates
  const getInteractionCounts = async (postId, userOverride = null) => {
    // Always fetch interactions to check liked status, even if counts are cached
    // This ensures likedPosts Set is properly populated on page load
    const userToCheck = userOverride || currentUser;
    
    try {
      const interactions = await makeAuthenticatedRequest(
        (token) => fetchPostInteractions(postId, token)
      );
      
      const counts = {
        likeCount: interactions.filter(
          (i) =>
            (i.interactionType === "like" || i.interaction_type === "like") &&
            !(i.parentId ?? i.parent_id)
        ).length,
        commentCount: interactions.filter(
          (i) => i.interactionType === "comment" || i.interaction_type === "comment"
        ).length,
      };

      // Check if current user has liked this post
      let isLiked = false;
      if (userToCheck) {
        const userLike = interactions.find(
          (i) =>
            (i.interactionType === "like" || i.interaction_type === "like") &&
            !(i.parentId ?? i.parent_id) &&
            (i.userId === userToCheck.user_id || i.user_id === userToCheck.user_id)
        );
        isLiked = !!userLike;
      }

      // Update counts cache
      setInteractionCounts((prev) => ({
        ...prev,
        [postId]: counts,
      }));

      // Return counts and liked status
      return { ...counts, isLiked };
    } catch (err) {
      console.error("Failed to fetch interaction counts:", err);
      // Return cached counts if available, otherwise return defaults
      const cachedCounts = interactionCounts[postId] || { likeCount: 0, commentCount: 0 };
      return { ...cachedCounts, isLiked: false };
    }
  };

  // Handle like action
  const handleLike = async (postId) => {
    if (!accessToken || !currentUser) return;

    const currentCounts = interactionCounts[postId] || { likeCount: 0, commentCount: 0 };
    const isCurrentlyLiked = likedPosts.has(postId);
    
    // Optimistic update
    if (isCurrentlyLiked) {
      // Unlike: decrease count and remove from likedPosts
      setInteractionCounts((prev) => ({
        ...prev,
        [postId]: { ...currentCounts, likeCount: Math.max(0, currentCounts.likeCount - 1) },
      }));
      setLikedPosts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    } else {
      // Like: increase count and add to likedPosts
      setInteractionCounts((prev) => ({
        ...prev,
        [postId]: { ...currentCounts, likeCount: currentCounts.likeCount + 1 },
      }));
      setLikedPosts((prev) => new Set([...prev, postId]));
    }

    try {
      // Use optimistic state to determine action (simpler logic)
      if (isCurrentlyLiked) {
        // Unlike: need to find and delete the existing like
        const interactions = await makeAuthenticatedRequest(
          (token) => fetchPostInteractions(postId, token)
        );
        
        const userLike = interactions.find(
          (i) =>
            (i.interactionType === "like" || i.interaction_type === "like") &&
            !(i.parentId ?? i.parent_id) &&
            (i.userId === currentUser.user_id || i.user_id === currentUser.user_id)
        );

        if (userLike) {
          await makeAuthenticatedRequest(async (token) => {
            await axios.delete(
              `${API_BASE_URL}/posts/interactions/${userLike.interactionId || userLike.interaction_id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                params: orgId ? { org_id: orgId } : {},
              }
            );
          });
        }
        // likedPosts already updated optimistically, no need to update again
      } else {
        // Like: create new like
        await makeAuthenticatedRequest(async (token) => {
          await axios.post(
            `${API_BASE_URL}/posts/${postId}/interactions`,
            { interaction_type: "like" },
            {
              headers: { Authorization: `Bearer ${token}` },
              params: orgId ? { org_id: orgId } : {},
            }
          );
        });
      }

      // Confirm with a forced network refresh so counts stay accurate
      const refreshed = await getInteractionCounts(postId, currentUser);
      setInteractionCounts((prev) => ({
        ...prev,
        [postId]: {
          likeCount: refreshed.likeCount,
          commentCount: refreshed.commentCount,
        },
      }));
      setLikedPosts((prev) => {
        const next = new Set(prev);
        if (refreshed.isLiked) {
          next.add(postId);
        } else {
          next.delete(postId);
        }
        return next;
      });
    } catch (err) {
      console.error("Failed to toggle like:", err);
      // Revert optimistic update
      setInteractionCounts((prev) => ({
        ...prev,
        [postId]: currentCounts,
      }));
      if (isCurrentlyLiked) {
        // Revert: add back to likedPosts
        setLikedPosts((prev) => new Set([...prev, postId]));
      } else {
        // Revert: remove from likedPosts
        setLikedPosts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
      }
      if (
        err.response?.status === 400 &&
        err.response?.data?.message?.includes("already liked")
      ) {
        const refreshed = await getInteractionCounts(postId, currentUser);
        setInteractionCounts((prev) => ({
          ...prev,
          [postId]: {
            likeCount: refreshed.likeCount,
            commentCount: refreshed.commentCount,
          },
        }));
        setLikedPosts((prev) => new Set([...prev, postId]));
      } else {
        toastError("Failed to update like. Please try again.");
      }
    }
  };

  // Handle comment / view thread → canonical permalink
  const handleComment = (post) => {
    const postId = post?.post_id || post?.postId;
    if (!orgId || postId == null) return;
    const href = permalinkForPost({ orgId, postId });
    const returnTo = pathWithPostAnchor(
      `${location.pathname}${location.search || ""}`,
      postId
    );
    navigate(withReturnQuery(href, returnTo));
  };

  // Handle join team
  const handleJoinTeam = async () => {
    if (!orgId || !team) return;
    setIsJoining(true);

    try {
      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.post(
          `${API_BASE_URL}/team/join`,
          {
            team_id: parseInt(teamId),
            org_id: orgId,
            role: "member",
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return response.data;
      });

      setUserMembershipStatus('member');
      // Refresh team data
      const teamData = await makeAuthenticatedRequest(fetchTeamDetails);
      setTeam(teamData);
    } catch (err) {
      console.error("Failed to join team:", err);
      toastError(err, "Failed to join team");
    } finally {
      setIsJoining(false);
    }
  };

  // Handle follow team
  const handleFollowTeam = async () => {
    if (!orgId || !team) return;
    if (team.visibility === "restricted") {
      toastError("Restricted teams cannot be followed. Request to join or ask for an invite.");
      return;
    }
    setIsFollowing(true);

    try {
      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.post(
          `${API_BASE_URL}/team/follow`,
          {
            team_id: parseInt(teamId),
            org_id: orgId,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return response.data;
      });

      setUserMembershipStatus('following');
    } catch (err) {
      console.error("Failed to follow team:", err);
      toastError(err, "Failed to follow team");
    } finally {
      setIsFollowing(false);
    }
  };

  // Handle request to join (for restricted searchable teams)
  const handleRequestToJoin = async () => {
    if (!orgId || !team) return;
    setIsRequesting(true);

    try {
      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.post(
          `${API_BASE_URL}/team/request-join`,
          {
            team_id: parseInt(teamId),
            org_id: orgId,
            role: "member",
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return response.data;
      });

      toastSuccess("Join request sent successfully");
    } catch (err) {
      console.error("Failed to request join:", err);
      toastError(err, "Failed to send join request");
    } finally {
      setIsRequesting(false);
    }
  };

  // Handle leave team
  const handleLeaveTeam = async () => {
    if (!orgId || !team) return;
    setIsLeaving(true);

    try {
      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.delete(
          `${API_BASE_URL}/team/leave`,
          {
            headers: { Authorization: `Bearer ${token}` },
            data: {
              team_id: parseInt(teamId),
              org_id: orgId,
            },
          }
        );
        return response.data;
      });

      setUserMembershipStatus('none');
      setShowLeaveDialog(false);
      // Refresh team data
      const teamData = await makeAuthenticatedRequest(fetchTeamDetails);
      setTeam(teamData);
      toastSuccess("Successfully left the team");
    } catch (err) {
      console.error("Failed to leave team:", err);
      toastError(err, "Failed to leave team");
    } finally {
      setIsLeaving(false);
    }
  };

  // Handle unfollow team
  const handleUnfollowTeam = async () => {
    if (!orgId || !team) return;
    setIsUnfollowing(true);

    try {
      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.delete(
          `${API_BASE_URL}/team/unfollow`,
          {
            headers: { Authorization: `Bearer ${token}` },
            data: {
              team_id: parseInt(teamId),
              org_id: orgId,
            },
          }
        );
        return response.data;
      });

      setUserMembershipStatus('none');
      setShowUnfollowDialog(false);
      toastSuccess("Successfully unfollowed the team");
    } catch (err) {
      console.error("Failed to unfollow team:", err);
      toastError(err, "Failed to unfollow team");
    } finally {
      setIsUnfollowing(false);
    }
  };

  // Sync activeTab with URL params when they change externally (e.g., browser back/forward)
  // Legacy tab=posts → feed; tab=settings → dedicated settings page
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");

    if (tabFromUrl === "settings" && orgId && teamId) {
      navigate(
        `/teams/${encodeURIComponent(teamId)}/settings?org_id=${encodeURIComponent(orgId)}`,
        { replace: true }
      );
      return;
    }

    const normalized = normalizeTeamTab(tabFromUrl);

    if (tabFromUrl === "posts") {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("tab", "feed");
      setSearchParams(newSearchParams, { replace: true });
    }

    setActiveTab((currentTab) =>
      normalized !== currentTab ? normalized : currentTab
    );
  }, [searchParams, setSearchParams, navigate, orgId, teamId]);

  // Deep-link: ?card=postId → kanban tab, select sprint (or All), highlight + scroll (no edit dialog)
  useEffect(() => {
    const cardIdFromUrl = searchParams.get("card");
    if (!cardIdFromUrl) {
      cardDeepLinkHandledRef.current = null;
      return;
    }
    if (loading || !accessToken || !orgId) {
      return;
    }
    if (cardDeepLinkHandledRef.current === String(cardIdFromUrl)) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      cardDeepLinkHandledRef.current = String(cardIdFromUrl);
      setActiveTab("kanban");

      const urlSprintRaw = searchParams.get("sprint_id");
      let sprintId =
        urlSprintRaw != null && urlSprintRaw !== ""
          ? Number(urlSprintRaw)
          : null;
      if (!Number.isFinite(sprintId) || sprintId <= 0) sprintId = null;

      let matchingCard = posts.find((post) => {
        const id = post.post_id || post.postId;
        return String(id) === String(cardIdFromUrl);
      });

      if (!matchingCard || sprintId == null) {
        try {
          const allData = await makeAuthenticatedRequest((token) =>
            fetchTeamPosts(token, {})
          );
          if (cancelled) return;
          const allPosts = Array.isArray(allData)
            ? allData
            : allData?.posts || [];
          matchingCard =
            allPosts.find((post) => {
              const id = post.post_id || post.postId;
              return String(id) === String(cardIdFromUrl);
            }) || matchingCard;
          if (matchingCard && sprintId == null) {
            const sid = matchingCard.sprint_id ?? matchingCard.sprintId;
            sprintId =
              sid != null && Number(sid) > 0 ? Number(sid) : null;
          }
        } catch (err) {
          console.error("Failed to resolve card deep link:", err);
        }
      }

      if (cancelled) return;

      if (!matchingCard) {
        toastError("That card could not be found on this board");
        const clearParams = new URLSearchParams(searchParams);
        clearParams.set("tab", "kanban");
        clearParams.delete("card");
        clearParams.delete("sprint_id");
        setSearchParams(clearParams, { replace: true });
        setSprintFilterMode("all");
        await refreshTeamPosts({ sprintFilterMode: "all" });
        return;
      }

      let list = sprints;
      if (!list.length) {
        list = await refreshSprints();
        if (cancelled) return;
      }

      if (sprintId) {
        const idx = list.findIndex(
          (sp) => Number(sp.sprint_id ?? sp.sprintId) === Number(sprintId)
        );
        if (idx >= 0) {
          setSprintIndex(idx);
          setSprintFilterMode("sprint");
          await refreshTeamPosts({
            sprintFilterMode: "sprint",
            sprintIndex: idx,
            sprints: list,
          });
        } else {
          setSprintFilterMode("all");
          await refreshTeamPosts({ sprintFilterMode: "all" });
        }
      } else {
        setSprintFilterMode("all");
        await refreshTeamPosts({ sprintFilterMode: "all" });
      }

      if (cancelled) return;

      setHighlightedCardId(cardIdFromUrl);
      setInspectTarget({
        id: String(cardIdFromUrl),
        token: Date.now(),
        returnTab: activeTab !== "kanban" ? activeTab : null,
        returnPath: safeReturnPath(searchParams.get("return")),
      });
      const clearParams = new URLSearchParams(searchParams);
      clearParams.set("tab", "kanban");
      clearParams.delete("card");
      clearParams.delete("sprint_id");
      setSearchParams(clearParams, { replace: true });

      const scrollToCard = (attempt = 0) => {
        const el = document.getElementById(
          kanbanCardAnchorId(cardIdFromUrl)
        );
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        if (attempt < 12) {
          window.setTimeout(() => scrollToCard(attempt + 1), 50);
        }
      };
      requestAnimationFrame(() => scrollToCard(0));
      window.setTimeout(() => {
        setHighlightedCardId((cur) =>
          String(cur) === String(cardIdFromUrl) ? null : cur
        );
      }, 1600);
    };

    run();
    return () => {
      cancelled = true;
    };
    // Intentionally omit posts/sprints from deps — run once per card query param
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, accessToken, orgId, searchParams.get("card")]);

  // Legacy ?post= → canonical permalink
  useEffect(() => {
    const postIdFromUrl = searchParams.get("post");
    if (!postIdFromUrl || !orgId) return;
    const params = new URLSearchParams();
    if (searchParams.get("from") === "catchup") {
      params.set("from", "catchup");
    }
    const returnTo = searchParams.get("return");
    if (returnTo) params.set("return", returnTo);
    const qs = params.toString();
    navigate(
      `${permalinkForPost({ orgId, postId: postIdFromUrl })}${qs ? `?${qs}` : ""}`,
      { replace: true }
    );
  }, [searchParams, orgId, navigate]);

  const returnPathForBoard = `${location.pathname}${location.search || ""}`;

  const runBoardSummarize = async ({ force }) => {
    if (!accessToken || !orgId || !teamId || boardSummarizing) return;
    setBoardSummarizing(true);
    setBoardSummarizeError(null);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/catchup/generate`,
        {
          scope: "board",
          team_id: Number(teamId),
          force: force === true,
        },
        {
          params: { org_id: orgId },
          headers: { Authorization: `Bearer ${accessToken}` },
          withCredentials: true,
        }
      );
      const briefing = data?.summary?.summary_json || null;
      setBoardBriefing(briefing);
      setBoardSummarizeMeta({
        fromCache: Boolean(data?.from_cache),
        reason: data?.reason || null,
        generatedAt: data?.summary?.generated_at || null,
      });
    } catch (err) {
      if (getEntitlementCode(err)) {
        const msg = entitlementMessageFor(err, {
          canManageSettings: canManageOrgSettings,
        });
        setBoardSummarizeError(msg);
        toastEntitlementOrError(err, "Failed to summarize board.", {
          canManageSettings: canManageOrgSettings,
          orgId,
        });
      } else {
        setBoardSummarizeError(
          messageForCatchupGenerateError(err, {
            fallback: "Failed to summarize board.",
          })
        );
      }
    } finally {
      setBoardSummarizing(false);
    }
  };

  // Load all data
  useEffect(() => {
    if (!accessToken || !teamId || !orgId) {
      if (!orgId) {
        setError("Organization ID (org_id) is required. Please navigate from an organization page.");
      }
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setBoardBriefing(null);
        setBoardSummarizeMeta(null);
        setBoardSummarizeError(null);

        // Fetch team details
        const teamData = await makeAuthenticatedRequest(fetchTeamDetails);
        setTeam(teamData);
        // Set allowNonMemberIntake from team data
        if (teamData) {
          setAllowNonMemberIntake(teamData.allowNonMemberIntake || false);
        }

        // Fetch organization details
        const orgData = await makeAuthenticatedRequest(fetchOrgDetails);
        if (orgData) {
          setOrgName(orgData.name);
          setOrgOwnedBy(orgData.owned_by || null);
        }

        try {
          const meRes = await axios.get(`${API_BASE_URL}/org/${orgId}/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const caps = new Set(meRes.data?.capabilities || []);
          const isOwner = Boolean(meRes.data?.is_owner);
          setCanOrgPostCritical(isOwner || caps.has("org.post"));
          setCanManageOrgSettings(
            isOwner ||
              meRes.data?.role === "admin" ||
              caps.has("org.manage_settings")
          );
          setCanIntegrations(isOwner || caps.has("org.integrations"));
        } catch {
          setCanOrgPostCritical(false);
          setCanManageOrgSettings(false);
          setCanIntegrations(false);
        }

        // Membership + current user before content fetches
        const status = await makeAuthenticatedRequest(checkUserMembershipStatus);
        setUserMembershipStatus(status);

        const userData = await makeAuthenticatedRequest(fetchCurrentUser);
        setCurrentUser(userData);

        const isOwner =
          !!userData?.user_id &&
          !!orgData?.owned_by &&
          userData.user_id === orgData.owned_by;
        const canLoadTeamContent =
          status === "member" ||
          status === "following" ||
          teamData?.visibility === "organization" ||
          isOwner;

        let postsList = [];
        let feedList = [];

        if (canLoadTeamContent) {
          // Fetch board posts + ranked feed in parallel
          try {
            const [postsData, feedData] = await Promise.all([
              makeAuthenticatedRequest(fetchTeamPosts),
              makeAuthenticatedRequest(fetchTeamFeed),
            ]);
            postsList = applyTeamPostsResponse(postsData);
            feedList = applyTeamFeedResponse(feedData);
          } catch (contentErr) {
            console.error("Failed to load team posts:", contentErr);
            setPosts([]);
            setFeedPosts([]);
            setHandoffStubs([]);
          }

          try {
            const [channel, handoffs] = await Promise.all([
              makeAuthenticatedRequest(fetchDefaultChannel),
              makeAuthenticatedRequest(fetchInboundHandoffs).catch(() => []),
            ]);
            setDefaultChannel(channel);
            setInboundHandoffs(handoffs);
          } catch (homeErr) {
            console.error("Failed to load team home extras:", homeErr);
            setDefaultChannel(null);
            setInboundHandoffs([]);
          }

          // Fetch kanban lanes
          setLoadingLanes(true);
          try {
            const lanesData = await makeAuthenticatedRequest(fetchKanbanLanes);
            let lanesArray = lanesData;
            if (lanesData && typeof lanesData === 'object' && !Array.isArray(lanesData)) {
              lanesArray = lanesData.data || lanesData.lanes || lanesData;
            }
            const filteredLanes = Array.isArray(lanesArray)
              ? lanesArray.filter(lane => !lane.team_id || lane.team_id === parseInt(teamId))
              : [];
            setKanbanLanes(filteredLanes);
          } catch (err) {
            console.error("Failed to fetch kanban lanes:", err);
            if (kanbanLanes.length === 0) {
              setKanbanLanes([]);
            }
          } finally {
            setLoadingLanes(false);
          }

          const kanbanOn =
            teamData?.kanbanEnabled !== false &&
            teamData?.kanban_enabled !== false;
          if (kanbanOn) {
            try {
              const list = await makeAuthenticatedRequest(fetchSprints);
              setSprints(list);
              const deepLinkCard = new URLSearchParams(window.location.search).get(
                "card"
              );
              // Defer current-sprint filter when opening a specific card from deep link
              if (!deepLinkCard) {
                const cur = findCurrentSprintIndex(list);
                if (cur >= 0) {
                  setSprintIndex(cur);
                  setSprintFilterMode("sprint");
                  const sid = list[cur]?.sprint_id ?? list[cur]?.sprintId;
                  if (sid) {
                    const filtered = await makeAuthenticatedRequest((token) =>
                      fetchTeamPosts(token, { sprintId: sid })
                    );
                    applyTeamPostsResponse(filtered);
                  }
                }
              }
            } catch (err) {
              console.error("Failed to load sprints:", err);
              setSprints([]);
            }
          }
        } else {
          // Restricted / invite-only: show page chrome + request-to-join, not API errors
          setPosts([]);
          setFeedPosts([]);
          setHandoffStubs([]);
          setKanbanLanes([]);
          setDefaultChannel(null);
          setInboundHandoffs([]);
        }

        // Members list is visible to org members even for restricted teams
        try {
          const membersData = await makeAuthenticatedRequest(fetchTeamMembers);
          const mappedMembers = membersData.map(item => ({
            ...item.user,
            role: item.role,
            overrides: item.overrides || [],
            title: item.title,
            org_email: item.org_email || null,
            member_avatar_url:
              item.member_avatar_url || item.user?.member_avatar_url || null,
            is_bot: Boolean(item.is_bot || item.user?.is_bot),
            bot_id: item.bot_id ?? item.user?.bot_id ?? null,
            installation_id:
              item.installation_id ?? item.user?.installation_id ?? null,
          }));
          setMembers(mappedMembers);
          setTeamMembers(mappedMembers);
        } catch (membersErr) {
          console.error("Failed to fetch team members:", membersErr);
          setMembers([]);
          setTeamMembers([]);
        }

        // Org members for title lookup on posts/comments
        try {
          const orgMembersResponse = await makeAuthenticatedRequest(async (token) => {
            return await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
              headers: { Authorization: `Bearer ${token}` },
            });
          });
          setOrgMembers(orgMembersResponse.data || []);
        } catch (orgMembersErr) {
          console.error("Failed to fetch org members:", orgMembersErr);
          setOrgMembers([]);
        }

        // Interaction counts for board + feed posts (deduped by id)
        const postsById = new Map();
        [...postsList, ...feedList].forEach((post) => {
          const id = post.post_id || post.postId;
          if (id != null && !postsById.has(id)) {
            postsById.set(id, post);
          }
        });
        const uniquePosts = Array.from(postsById.values());
        if (uniquePosts.length > 0) {
          const countsPromises = uniquePosts.map((post) =>
            getInteractionCounts(post.post_id || post.postId, userData)
          );
          const results = await Promise.all(countsPromises);

          const likedPostIds = results
            .map((result, index) => ({
              postId: uniquePosts[index].post_id || uniquePosts[index].postId,
              isLiked: result.isLiked,
            }))
            .filter((item) => item.isLiked)
            .map((item) => item.postId);
          setLikedPosts(new Set(likedPostIds));
        } else {
          setLikedPosts(new Set());
        }

        setLoading(false);
      } catch (err) {
        console.error("Failed to load team data:", err);
        setError(
          err.response?.data?.message || err.message || "Failed to load team data"
        );
        setLoading(false);
      }
    };

    loadData();
    // Mount/reload when team or auth identity changes; omit unstable request helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, teamId, orgId]);

  // userId -> org membership title for post/comment taglines
  const orgMembersMap = useMemo(() => {
    const map = {};
    (orgMembers || []).forEach((member) => {
      const userId = member.user?.user_id || member.user_id;
      if (userId) {
        map[userId] = member.title || null;
      }
    });
    return map;
  }, [orgMembers]);

  const filteredTeamMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((member) => {
      const haystack = [
        member.first_name,
        member.last_name,
        getFullName(member),
        member.org_email,
        member.title,
        member.role,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [members, memberSearch]);

  const completedWorkLaneName = useMemo(() => {
    const id =
      team?.completed_work_lane_id ?? team?.completedWorkLaneId ?? null;
    if (id == null) return null;
    const lane = kanbanLanes.find(
      (l) => (l.lane_id ?? l.laneId) === id || String(l.lane_id) === String(id)
    );
    return lane?.name || null;
  }, [team, kanbanLanes]);

  const allKanbanPosts = useMemo(() => {
    const byId = new Map();
    for (const p of [...(roadmapPosts || []), ...(posts || [])]) {
      const id = Number(p.post_id || p.postId);
      if (!id || !(p.kanbanStatus || p.kanban_status)) continue;
      if (!byId.has(id)) byId.set(id, p);
    }
    return [...byId.values()];
  }, [posts, roadmapPosts]);

  const overdueCards = useMemo(
    () =>
      allKanbanPosts.filter((card) =>
        isCardOverdue(card, completedWorkLaneName)
      ),
    [allKanbanPosts, completedWorkLaneName]
  );

  const parentPickerOptions = useMemo(() => {
    const kind = cardFormData.work_kind || "task";
    const selfId = selectedCard
      ? Number(selectedCard.post_id || selectedCard.postId)
      : null;
    return allKanbanPosts.filter((p) => {
      const id = Number(p.post_id || p.postId);
      if (selfId && id === selfId) return false;
      const k = workKindOf(p);
      if (kind === "story") return k === "epic";
      if (kind === "task") return k === "epic" || k === "story";
      return false;
    });
  }, [allKanbanPosts, cardFormData.work_kind, selectedCard]);

  const teamMemberIds = useMemo(() => {
    const ids = new Set();
    for (const m of teamMembers || []) {
      if (m.is_bot || m.role === "bot") continue;
      const id = m.user_id || m.userId || m.user?.user_id || m.user?.userId;
      if (id) ids.add(String(id));
    }
    return ids;
  }, [teamMembers]);

  useScrollToFeedPostAnchor(!loading && activeTab === "feed");

  // Unfiltered board posts: roadmap + parent/epic pickers (sprint filter only affects lanes)
  useEffect(() => {
    if (!accessToken || !orgId || !teamId) return;
    const kanbanOn =
      !!team &&
      team.kanbanEnabled !== false &&
      team.kanban_enabled !== false;
    if (!kanbanOn) return;
    loadRoadmapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, orgId, teamId, team]);

  // Kanban off: leave board tab / deep-links (must run before early returns)
  useEffect(() => {
    const kanbanOn =
      !!team &&
      team.kanbanEnabled !== false &&
      team.kanban_enabled !== false;
    if (!team || kanbanOn) return;
    if (activeTab !== "kanban" && activeTab !== "roadmap" && !searchParams.get("card")) return;
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", "feed");
    newSearchParams.delete("card");
    setSearchParams(newSearchParams, { replace: true });
    setActiveTab("feed");
  }, [team, activeTab, searchParams, setSearchParams]);

  if (loading) {
    return (
      <div className="flex flex-col bg-background text-foreground min-h-screen">
        <div className="flex-1 p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-4 w-96 mb-6" />
          <Skeleton className="h-10 w-32 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col bg-background text-foreground min-h-screen">
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate(orgId ? `/orgs/${orgId}` : "/dashboard")} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to {orgName || (orgId ? "Organization" : "dashboard")}
          </Button>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="flex flex-col bg-background text-foreground min-h-screen">
        <div className="flex-1 p-6">
          <div className="text-muted-foreground">Team not found</div>
          <Button onClick={() => navigate(orgId ? `/orgs/${orgId}` : "/dashboard")} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  const canCreatePost = userMembershipStatus === "member";
  const canCreateCard =
    userMembershipStatus === "member" ||
    (userMembershipStatus !== "member" &&
      allowNonMemberIntake &&
      team?.visibility === "organization" &&
      team?.intake_lane_id != null);

  // Handle kanban card move
  const handleCardMove = async (cardId, newStatus, newPosition) => {
    try {
      // Ensure cardId is a number
      const numericCardId = typeof cardId === 'string' ? parseInt(cardId, 10) : cardId;
      
      if (isNaN(numericCardId)) {
        throw new Error(`Invalid card ID: ${cardId}`);
      }

      const movingCard = posts.find(
        (p) => (p.post_id || p.postId) === numericCardId
      );
      if (movingCard?.handoff_frozen || movingCard?.pending_handoff) {
        toastError("This card is frozen while a handoff is pending.");
        await refreshTeamPosts();
        return;
      }

      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.patch(
          `${API_BASE_URL}/posts/${numericCardId}`,
          {
            kanban_status: newStatus,
            kanban_position: newPosition,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { org_id: orgId },
          }
        );
        return response.data;
      });

      // Refresh posts to get updated data
      await refreshTeamPosts();
    } catch (err) {
      console.error("Failed to move card:", err);
      console.error("Error details:", {
        cardId,
        numericCardId: typeof cardId === 'string' ? parseInt(cardId, 10) : cardId,
        newStatus,
        newPosition,
        response: err.response?.data,
        status: err.response?.status,
      });
      const errorMessage = err.response?.data?.message || err.message || "Failed to move card";
      toastError(errorMessage);
      // Refresh posts to revert optimistic update
      await refreshTeamPosts();
    }
  };

  // Handle create kanban card
  const handleCreateCard = (preset = {}) => {
    const intakeLane = kanbanLanes.find(
      (l) => l.lane_id === (team?.intake_lane_id ?? null)
    );
    // Members pick any lane; non-members default to intake when configured
    let defaultStatus =
      kanbanLanes.length > 0
        ? [...kanbanLanes].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))[0]
            .name
        : "Backlog";
    if (userMembershipStatus !== "member" && intakeLane) {
      defaultStatus = intakeLane.name;
    }

    setCardFormData({
      title: "",
      content: "",
      kanban_status: defaultStatus,
      assignee_ids: [],
      starts_at: "",
      due_date: "",
      priority: "medium",
      sprint_id: "",
      work_kind: preset.work_kind || "task",
      parent_id: preset.parent_id ? String(preset.parent_id) : "",
      story_points: "",
    });
    setCardPendingFiles([]);
    setShowCreateCardDialog(true);
  };

  // Handle submit create team post (non-kanban) from Feed composer
  const handleSubmitCreatePost = async (payload) => {
    if (isCreatingPost) return;
    const title = payload?.title?.trim() || "";
    const content = payload?.content?.trim();
    if (!content) {
      toastError("Content is required");
      return;
    }

    setIsCreatingPost(true);
    try {
      const created = await makeAuthenticatedRequest(async (token) => {
        const response = await axios.post(
          `${API_BASE_URL}/posts`,
          {
            team_id: parseInt(teamId, 10),
            org_id: orgId,
            title,
            content,
            tags: payload.tags?.trim() || undefined,
            critical: Boolean(payload.critical),
            references: payload.references,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return response.data;
      });
      const createdPostId = created?.post_id ?? created?.postId;
      let withAttachments = created;
      if (createdPostId != null && payload.files?.length) {
        try {
          const attachments = await uploadPostAttachments({
            postId: createdPostId,
            orgId,
            files: payload.files,
            accessToken,
          });
          withAttachments = { ...created, attachments };
        } catch (attachErr) {
          console.error("Failed to upload post attachments:", attachErr);
          toastEntitlementOrError(
            attachErr,
            "Post created, but file upload failed",
            { canManageSettings: canManageOrgSettings, orgId }
          );
        }
      }
      const withAuthor =
        withAttachments && !withAttachments.user && currentUser
          ? { ...withAttachments, user: currentUser }
          : withAttachments;

      setComposerFormKey((k) => k + 1);
      await refreshTeamPosts();
      setFeedPosts((prev) => prependCreatedPost(prev, withAuthor));
      const newId = withAuthor?.post_id ?? withAuthor?.postId;
      if (newId != null) {
        setHighlightedPostId(newId);
        window.setTimeout(() => {
          setHighlightedPostId((cur) =>
            String(cur) === String(newId) ? null : cur
          );
        }, 1600);
        requestAnimationFrame(() => {
          const el = document.getElementById(feedPostAnchorId(newId));
          el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      }
    } catch (err) {
      console.error("Failed to create post:", err);
      toastError(err, "Failed to create post");
      throw err;
    } finally {
      setIsCreatingPost(false);
    }
  };

  const handleSaveDraftPost = async (payload) => {
    setIsCreatingPost(true);
    try {
      const created = await makeAuthenticatedRequest(async (token) => {
        const response = await axios.post(
          `${API_BASE_URL}/posts`,
          {
            team_id: parseInt(teamId, 10),
            org_id: orgId,
            title: payload?.title || "",
            content: payload?.content || "",
            tags: payload.tags?.trim() || undefined,
            critical: Boolean(payload.critical),
            references: payload.references,
            is_draft: true,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return response.data;
      });
      const draftId = created?.post_id ?? created?.postId;
      if (draftId != null && payload.files?.length) {
        try {
          await uploadPostAttachments({
            postId: draftId,
            orgId,
            files: payload.files,
            accessToken,
          });
        } catch (attachErr) {
          console.error("Failed to upload draft attachments:", attachErr);
          toastEntitlementOrError(
            attachErr,
            "Draft saved, but file upload failed",
            { canManageSettings: canManageOrgSettings, orgId }
          );
        }
      }
      setComposerFormKey((k) => k + 1);
      toastSuccess("Draft saved");
    } catch (err) {
      console.error("Failed to save draft:", err);
      toastError(err, "Failed to save draft");
      throw err;
    } finally {
      setIsCreatingPost(false);
    }
  };

  // Handle submit create card
  const handleSubmitCreateCard = async () => {
    const content = normalizeEditorMarkdown(cardFormData.content);
    if (!cardFormData.title?.trim() || isMarkdownEmpty(content)) {
      toastError("Title and content are required");
      return;
    }

    setIsCreatingCard(true);
    try {
      const created = await makeAuthenticatedRequest(async (token) => {
        const response = await axios.post(
          `${API_BASE_URL}/posts`,
          {
            team_id: parseInt(teamId),
            org_id: orgId,
            title: cardFormData.title.trim(),
            content,
            kanban_status: cardFormData.kanban_status,
            kanban_position: 0,
            assignee_ids: Array.isArray(cardFormData.assignee_ids)
              ? cardFormData.assignee_ids
              : [],
            starts_at: dateInputToIso(cardFormData.starts_at),
            due_date: dateInputToIso(cardFormData.due_date),
            priority: cardFormData.priority,
            work_kind: cardFormData.work_kind || "task",
            parent_id:
              cardFormData.work_kind === "epic" || !cardFormData.parent_id
                ? undefined
                : Number(cardFormData.parent_id),
            story_points:
              cardFormData.work_kind === "epic" || !cardFormData.story_points
                ? undefined
                : Number(cardFormData.story_points),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return response.data;
      });

      const postId = created?.post_id ?? created?.postId;
      if (postId && cardPendingFiles.length > 0) {
        try {
          await makeAuthenticatedRequest(async (token) =>
            uploadPostAttachments({
              postId,
              orgId,
              files: cardPendingFiles,
              accessToken: token,
            })
          );
        } catch (attachErr) {
          console.error("Failed to upload card attachments:", attachErr);
          toastEntitlementOrError(
            attachErr,
            "Card created, but file upload failed",
            { canManageSettings: canManageOrgSettings, orgId }
          );
        }
      }

      setCardPendingFiles([]);
      setShowCreateCardDialog(false);
      await refreshTeamPosts();
      await loadRoadmapData();
    } catch (err) {
      console.error("Failed to create card:", err);
      toastError(err, "Failed to create card");
    } finally {
      setIsCreatingCard(false);
    }
  };

  const openKanbanTab = (card = null) => {
    const cardId = card ? postIdOfWork(card) : null;
    if (cardId) {
      // Peek in place: overlay is portaled, so stay on the current tab.
      setInspectTarget({
        id: String(cardId),
        token: Date.now(),
        returnTab: activeTab !== "kanban" ? activeTab : null,
      });
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("tab", "kanban");
    next.delete("card");
    setSearchParams(next);
  };

  const dismissCardInspect = () => {
    const target = inspectTargetRef.current;
    const returnTo = target?.returnPath;
    const returnTab = target?.returnTab;
    setInspectTarget(null);
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete("card");
    if (returnTab && returnTab !== "kanban") {
      next.set("tab", returnTab);
      setActiveTab(returnTab);
      setSearchParams(next, { replace: true });
      return;
    }
    if (searchParams.get("card")) {
      setSearchParams(next, { replace: true });
    }
  };

  const handleViewTeamChannel = () => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "feed");
    setSearchParams(next);
  };

  // Handle card click (view thread) → canonical permalink
  const handleCardClick = (card) => {
    const postId = card?.post_id || card?.postId;
    if (!orgId || postId == null) return;
    const href = permalinkForPost({ orgId, postId });
    const returnTo = `${location.pathname}${location.search || ""}`;
    navigate(
      withReturnQuery(
        href,
        activeTab === "feed"
          ? pathWithPostAnchor(returnTo, postId)
          : returnTo
      )
    );
  };

  // Handle card edit button click
  const handleCardEdit = (card) => {
    setSelectedCard(card);
    setShowCardDetailDialog(true);
  };

  const handleCancelPendingHandoff = async () => {
    const pending =
      selectedCard?.pending_handoff || selectedCard?.pendingHandoff;
    const handoffId = pending?.handoff_id ?? pending?.handoffId;
    if (!handoffId) return;

    setIsCancellingHandoff(true);
    try {
      await makeAuthenticatedRequest(async (token) => {
        await axios.post(
          `${API_BASE_URL}/posts/handoffs/${handoffId}/cancel`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { org_id: orgId },
          }
        );
      });
      const postsList = await refreshTeamPosts();
      const cardId = selectedCard.post_id || selectedCard.postId;
      const updated = postsList.find(
        (p) => (p.post_id || p.postId) === cardId
      );
      if (updated) {
        setSelectedCard(updated);
      }
    } catch (err) {
      console.error("Failed to cancel handoff:", err);
      toastError(err, "Failed to cancel handoff");
    } finally {
      setIsCancellingHandoff(false);
    }
  };

  const handleStubClick = (stub) => {
    const currentTeam = stub?.current_team || stub?.currentTeam;
    const targetTeamId = currentTeam?.team_id ?? currentTeam?.teamId;
    const postId = stub?.post_id ?? stub?.postId;
    if (!targetTeamId || !postId) return;
    const params = new URLSearchParams();
    if (orgId) params.set("org_id", orgId);
    params.set("tab", "kanban");
    params.set("card", String(postId));
    navigate(`/teams/${targetTeamId}?${params.toString()}`);
  };

  const handleDismissStub = async (stub) => {
    const handoffId = stub?.handoff_id ?? stub?.handoffId;
    if (!handoffId || !orgId) return;
    const title = stub?.title ? `"${stub.title}"` : "this stub";
    const ok = await confirm({
      title: "Remove from Handed off?",
      description: `Remove ${title} from this team’s Handed off lane? Handoff history on the card is kept.`,
      confirmLabel: "Remove",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await makeAuthenticatedRequest(async (token) => {
        await axios.post(
          `${API_BASE_URL}/posts/handoffs/${handoffId}/dismiss-stub`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { org_id: orgId },
          }
        );
      });
      await refreshTeamPosts();
      await loadRoadmapData();
    } catch (err) {
      console.error("Failed to dismiss handoff stub:", err);
      toastError(err, "Failed to remove stub");
    }
  };

  // Calculate current user's role from teamMembers
  const currentUserMember = teamMembers.find(
    (member) => member.user?.user_id === currentUser?.user_id || member.user_id === currentUser?.user_id
  );
  const userRole = currentUserMember?.role; // 'admin' | 'member' | undefined
  const userOverrides = currentUserMember?.overrides || [];
  const isOrgOwner =
    !!currentUser?.user_id &&
    !!orgOwnedBy &&
    currentUser.user_id === orgOwnedBy;
  const isTeamAdmin = userRole === "admin";
  const isTeamAdminEffective = isTeamAdmin || isOrgOwner;
  const canOpenTeamSettings = isTeamAdminEffective || canManageOrgSettings;
  const teamKanbanEnabled =
    !!team &&
    team.kanbanEnabled !== false &&
    team.kanban_enabled !== false;
  const hasStuckStrip =
    (teamKanbanEnabled && overdueCards.length > 0) ||
    (Array.isArray(inboundHandoffs) && inboundHandoffs.length > 0);
  const hasFeedAside = !!defaultChannel || hasStuckStrip;
  const canManageLanes =
    isTeamAdminEffective || userOverrides.includes("kanban.manage_lanes");
  const canDeleteCards =
    isTeamAdminEffective || userOverrides.includes("cards.delete");

  const handleSprintFilterChange = async (mode) => {
    setSprintFilterMode(mode);
    await refreshTeamPosts({ sprintFilterMode: mode });
  };

  const handleSprintIndexChange = async (idx) => {
    setSprintIndex(idx);
    if (sprintFilterMode === "sprint") {
      await refreshTeamPosts({ sprintIndex: idx });
    }
  };

  const createSprintRequest = async (firstCadence) => {
    setStartingSprint(true);
    try {
      await makeAuthenticatedRequest(async (token) => {
        await axios.post(
          `${API_BASE_URL}/team/${teamId}/sprints`,
          {
            org_id: orgId,
            ...(firstCadence
              ? {
                  sprint_length_days: firstCadence.sprint_length_days,
                  sprint_start_weekday: firstCadence.sprint_start_weekday,
                  ...(firstCadence.starts_at
                    ? { starts_at: firstCadence.starts_at }
                    : {}),
                }
              : {}),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      });
      if (firstCadence) {
        const teamData = await makeAuthenticatedRequest(fetchTeamDetails);
        setTeam(teamData);
      }
      const list = await refreshSprints();
      const last = Math.max(0, list.length - 1);
      setSprintIndex(last);
      setSprintFilterMode("sprint");
      await refreshTeamPosts({
        sprints: list,
        sprintIndex: last,
        sprintFilterMode: "sprint",
      });
      toastSuccess(list.length === 1 ? "Sprint 1 started" : "Sprint created");
      setShowSprintCadenceDialog(false);
    } catch (err) {
      toastError(err, "Could not create sprint");
    } finally {
      setStartingSprint(false);
    }
  };

  const handleStartSprint = async () => {
    if (sprints.length === 0) {
      const cadence = cadenceFormValues(team);
      setSprintCadenceDraft({
        ...cadence,
        starts_at: latestAllowedFirstSprintStart(new Date(), {
          lengthDays: cadence.sprint_length_days,
          startWeekday: cadence.sprint_start_weekday,
        }),
      });
      setShowSprintCadenceDialog(true);
      return;
    }
    await createSprintRequest();
  };

  const handleSaveAndStartSprint = async () => {
    await createSprintRequest(sprintCadenceDraft);
  };

  const openRenameSprint = () => {
    const sp = sprints[sprintIndex];
    if (!sp) return;
    setRenameSprintName(sp.name || "");
    setShowRenameSprintDialog(true);
  };

  const handleRenameSprint = async () => {
    const sp = sprints[sprintIndex];
    const sid = sp?.sprint_id ?? sp?.sprintId;
    const name = renameSprintName.trim();
    if (!sid || !name) return;
    setRenamingSprint(true);
    try {
      await makeAuthenticatedRequest(async (token) => {
        await axios.patch(
          `${API_BASE_URL}/team/${teamId}/sprints/${sid}`,
          { org_id: orgId, name },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });
      await refreshSprints();
      setShowRenameSprintDialog(false);
      toastSuccess("Sprint renamed");
    } catch (err) {
      toastError(err, "Could not rename sprint");
    } finally {
      setRenamingSprint(false);
    }
  };

  const refreshMembersList = async () => {
    try {
      const membersData = await makeAuthenticatedRequest(fetchTeamMembers);
      const mappedMembers = membersData.map((item) => ({
        ...item.user,
        role: item.role,
        overrides: item.overrides || [],
        title: item.title,
        org_email: item.org_email || null,
        member_avatar_url:
          item.member_avatar_url || item.user?.member_avatar_url || null,
        is_bot: Boolean(item.is_bot || item.user?.is_bot),
        bot_id: item.bot_id ?? item.user?.bot_id ?? null,
        installation_id:
          item.installation_id ?? item.user?.installation_id ?? null,
      }));
      setMembers(mappedMembers);
      setTeamMembers(mappedMembers);
    } catch (err) {
      console.error("Failed to refresh members:", err);
    }
  };

  const openMemberPermissions = (member) => {
    setMemberPermissionsTarget(member);
    setMemberPermissionsForm({
      role: member.role === "admin" ? "admin" : "member",
      overrides: [...(member.overrides || [])],
    });
  };

  const closeMemberPermissions = () => {
    if (isSavingMemberPermissions) return;
    setMemberPermissionsTarget(null);
  };

  const toggleMemberOverride = (key) => {
    setMemberPermissionsForm((prev) => {
      const next = new Set(prev.overrides);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, overrides: [...next] };
    });
  };

  const handleSaveMemberPermissions = async () => {
    if (!orgId || !teamId || !memberPermissionsTarget) return;
    setIsSavingMemberPermissions(true);
    try {
      const body = {
        role: memberPermissionsForm.role,
        // Admins do not need grant overrides; clear them on save
        overrides:
          memberPermissionsForm.role === "admin"
            ? []
            : memberPermissionsForm.overrides,
      };
      await makeAuthenticatedRequest(async (token) => {
        await axios.patch(
          `${API_BASE_URL}/team/${teamId}/members/${memberPermissionsTarget.user_id}?org_id=${orgId}`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });
      await refreshMembersList();
      setMemberPermissionsTarget(null);
    } catch (err) {
      console.error("Failed to update member:", err);
      toastError(err, "Failed to update member permissions");
    } finally {
      setIsSavingMemberPermissions(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!orgId || !teamId || !removeMemberTarget?.userId) return;
    setIsRemovingMember(true);
    try {
      await makeAuthenticatedRequest(async (token) => {
        await axios.delete(
          `${API_BASE_URL}/team/${teamId}/members/${removeMemberTarget.userId}?org_id=${orgId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });
      await refreshMembersList();
      setMemberPermissionsTarget(null);
      setShowRemoveMemberDialog(false);
      setRemoveMemberTarget(null);
    } catch (err) {
      console.error("Failed to remove member:", err);
      toastError(err, "Failed to remove member");
    } finally {
      setIsRemovingMember(false);
    }
  };

  const changeCardWorkKind = (next) => {
    setCardFormData((prev) => {
      let parent_id = prev.parent_id;
      if (next === "epic") {
        parent_id = "";
      } else if (parent_id) {
        const parent = allKanbanPosts.find(
          (p) => String(p.post_id || p.postId) === String(parent_id)
        );
        const pk = parent ? workKindOf(parent) : null;
        if (next === "story" && pk !== "epic") parent_id = "";
        if (next === "task" && pk !== "epic" && pk !== "story") parent_id = "";
      }
      return {
        ...prev,
        work_kind: next,
        parent_id,
        story_points: next === "epic" ? "" : prev.story_points,
        sprint_id: next === "epic" ? "" : prev.sprint_id,
      };
    });
  };

  const handleAddChildFromEdit = (kind) => {
    const parentId = selectedCard?.post_id || selectedCard?.postId;
    setShowCardDetailDialog(false);
    setSelectedCard(null);
    handleCreateCard({ work_kind: kind, parent_id: parentId });
  };

  const handleSoftDeleteCard = async () => {
    if (!selectedCard) return;
    const cardId = selectedCard.post_id || selectedCard.postId;
    const ok = await confirm({
      title: "Delete card?",
      description:
        "Soft-delete this card? It will be hidden from the board and feeds.",
      confirmLabel: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await makeAuthenticatedRequest(async (token) => {
        await axios.delete(`${API_BASE_URL}/posts/${cardId}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { org_id: orgId },
        });
      });
      setShowCardDetailDialog(false);
      setSelectedCard(null);
      setHighlightedCardId(null);
      await refreshTeamPosts();
      await loadRoadmapData();
    } catch (err) {
      console.error("Failed to delete card:", err);
      toastError(err, "Failed to delete card");
    }
  };

  return (
    <div className="flex flex-col bg-background text-foreground min-h-0 flex-1 w-full max-w-full min-w-0">
      <header className="flex items-center gap-2 border-b border-border px-3 py-2 md:gap-4 md:p-4 min-w-0">
        <Button
          variant="ghost"
          className="min-w-0 max-w-full h-10 px-2 md:h-9 md:px-3"
          onClick={() => {
            const returnTo = safeReturnPath(searchParams.get("return"));
            if (returnTo) {
              navigate(returnTo);
              return;
            }
            navigate(orgId ? `/orgs/${orgId}` : "/dashboard");
          }}
        >
          <ArrowLeft className="h-4 w-4 md:mr-2 shrink-0" />
          <span className="hidden truncate md:inline">
            Back to {orgName || (orgId ? "Organization" : "dashboard")}
          </span>
          <span className="sr-only md:hidden">
            Back to {orgName || (orgId ? "Organization" : "dashboard")}
          </span>
        </Button>
      </header>
      <div className="flex-1 px-4 py-3 md:p-6 overflow-y-auto min-w-0 max-w-full">
        {/* Team Header */}
        <div className="mb-6 min-w-0">
          <div className="flex flex-col gap-3 mb-4">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold break-words md:text-3xl">{team.name}</h1>
                {canOpenTeamSettings ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
                          aria-label="Team settings"
                          data-tour="team-settings"
                          onClick={() => {
                            if (!orgId) return;
                            navigate(
                              `/teams/${encodeURIComponent(teamId)}/settings?org_id=${encodeURIComponent(orgId)}`
                            );
                          }}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Team settings</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null}
              </div>
              <p className="text-muted-foreground mb-2 break-words">{team.description || "No description"}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {userMembershipStatus === 'none' && (
                <Badge variant="outline">Non-member</Badge>
              )}
              {userMembershipStatus === 'none' && team.visibility === 'organization' && (
                <Button onClick={handleJoinTeam} disabled={isJoining}>
                  {isJoining ? "Joining..." : "Join Team"}
                </Button>
              )}
              {userMembershipStatus === 'none' && team.visibility === 'restricted' && (
                <Button onClick={handleRequestToJoin} disabled={isRequesting} variant="outline">
                  {isRequesting ? "Requesting..." : "Request to Join"}
                </Button>
              )}
              {userMembershipStatus === 'none' && team.visibility === 'organization' && (
                <Button onClick={handleFollowTeam} disabled={isFollowing} variant="outline">
                  {isFollowing ? "Following..." : "Follow"}
                </Button>
              )}
              {userMembershipStatus === 'member' && (
                <>
                  <Badge variant="default">
                    {userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : 'Member'}
                  </Badge>
                  <Button 
                    variant="destructive" 
                    onClick={() => setShowLeaveDialog(true)}
                    disabled={isLeaving}
                  >
                    {isLeaving ? "Leaving..." : "Leave Team"}
                  </Button>
                </>
              )}
              {userMembershipStatus === 'following' && (
                <>
                  <Badge variant="outline">Following</Badge>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowUnfollowDialog(true)}
                    disabled={isUnfollowing}
                  >
                    {isUnfollowing ? "Unfollowing..." : "Unfollow"}
                  </Button>
                </>
              )}
            </div>
          </div>

          {userMembershipStatus === "none" &&
            team.visibility === "restricted" &&
            !isOrgOwner && (
              <Alert className="mb-4">
                <AlertDescription>
                  This is a restricted team. You can request to join or ask a
                  member for an invitation. Team posts and boards stay hidden
                  until you&apos;re a member.
                </AlertDescription>
              </Alert>
            )}
          
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <span className="font-medium text-muted-foreground">Visibility: </span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-help">
                      <Badge variant={team.visibility === "organization" ? "default" : "destructive"}>
                        {team.visibility === "organization" ? "Organization-wide" : "Restricted"}
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm leading-snug">
                    {team.visibility === "organization"
                      ? TEAM_ORGANIZATION_TOOLTIP
                      : TEAM_RESTRICTED_TOOLTIP}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {team.tags && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-muted-foreground">Tags:</span>
                {team.tags.split(',').map((tag, idx) => (
                  <Badge key={idx} variant="secondary">{tag.trim()}</Badge>
                ))}
              </div>
            )}
            <span className="text-muted-foreground">
              Created: {new Date(team.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        <Tabs 
          value={activeTab} 
          className="w-full min-w-0"
          onValueChange={(value) => {
            setActiveTab(value);
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set("tab", value);
            newSearchParams.delete("card");
            setSearchParams(newSearchParams);
            if (value !== "kanban") setInspectTarget(null);
          }}
        >
          <TabsList className="max-w-full h-auto flex-wrap justify-start">
            <TabsTrigger value="feed">Feed ({feedPosts.length})</TabsTrigger>
            <TabsTrigger value="channels">Channels</TabsTrigger>
            {teamKanbanEnabled && (
              <>
                <TabsTrigger value="kanban">Kanban</TabsTrigger>
                <TabsTrigger value="roadmap">Roadmap</TabsTrigger>
              </>
            )}
            {(userMembershipStatus === "member" ||
              isOrgOwner ||
              userMembershipStatus === "following" ||
              team.visibility === "organization") && (
              <TabsTrigger value="files">Files</TabsTrigger>
            )}
            <TabsTrigger value="members">Members ({members.length})</TabsTrigger>
            <TabsTrigger value="about">About</TabsTrigger>
          </TabsList>

          {/* Feed Tab — ranked posts + non-silent cards */}
          <TabsContent value="feed" className="mt-4">
            <div className="space-y-4">
              <CatchUpCard
                orgId={orgId}
                scope="team"
                teamId={teamId}
                accessToken={accessToken}
                canManageSettings={canManageOrgSettings}
              />
              <div
                className={
                  hasFeedAside
                    ? "lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(20rem,24rem)] lg:gap-6 lg:items-start"
                    : "mx-auto w-full max-w-xl"
                }
              >
                <div
                  className={
                    hasFeedAside
                      ? "space-y-4 min-w-0"
                      : "space-y-4 min-w-0 max-w-xl"
                  }
                >
                  {canCreatePost && (
                    <PostComposer
                      mode="collapsed"
                      destinationMode="fixed"
                      fixedTeamId={parseInt(teamId, 10)}
                      orgId={orgId}
                      hideDestination
                      isSubmitting={isCreatingPost}
                      onSubmit={handleSubmitCreatePost}
                      onSaveDraft={handleSaveDraftPost}
                      formKey={composerFormKey}
                      allowCritical={
                        isTeamAdminEffective || canOrgPostCritical
                      }
                    />
                  )}
                  {feedPosts.length === 0 ? (
                    <p className="text-muted-foreground">
                      No activity in this team feed yet.
                    </p>
                  ) : (
                    <div className="divide-y-0">
                      {feedPosts.map((post) => {
                        const postId = post.post_id || post.postId;
                        const counts = interactionCounts[postId] || {
                          likeCount: 0,
                          commentCount: 0,
                        };
                        return (
                          <PostCard
                            key={postId}
                            post={post}
                            onExpand={() => handleComment(post)}
                            likeCount={counts.likeCount}
                            commentCount={counts.commentCount}
                            accessToken={accessToken}
                            onLike={() => handleLike(postId)}
                            onComment={() => handleComment(post)}
                            isLiked={likedPosts.has(postId)}
                            orgMembersMap={orgMembersMap}
                            teamMemberIds={teamMemberIds}
                            relativeTimestamps
                            highlighted={
                              highlightedPostId != null &&
                              String(highlightedPostId) === String(postId)
                            }
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
                {hasFeedAside ? (
                  <div className="mt-6 lg:mt-0 lg:sticky lg:top-4 min-w-0 space-y-4">
                    <TeamStuckStrip
                      overdueCards={overdueCards}
                      inboundHandoffs={inboundHandoffs}
                      kanbanEnabled={teamKanbanEnabled}
                      onOpenKanbanCard={openKanbanTab}
                      onOpenHandoffs={() => setShowInboundHandoffs(true)}
                      onOpenKanban={() => openKanbanTab()}
                    />
                    {defaultChannel ? (
                      <TeamDefaultChannel
                        orgId={orgId}
                        channel={defaultChannel}
                        onJoined={(joined) => setDefaultChannel(joined)}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </TabsContent>

          {/* Kanban Tab */}
          {teamKanbanEnabled && (
          <TabsContent
            value="kanban"
            forceMount={
              Boolean(inspectTarget) && activeTab !== "kanban" ? true : undefined
            }
            className="mt-4 min-w-0 max-w-full"
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {canManageLanes && (
                    <Button
                      variant="outline"
                      onClick={() => setShowLaneManager(true)}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Lanes
                    </Button>
                  )}
                {userMembershipStatus === "member" && (
                  <Button
                    variant="outline"
                    onClick={() => setShowInboundHandoffs(true)}
                  >
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Handoffs
                  </Button>
                )}
              </div>
              {!boardBriefing ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={boardSummarizing}
                  onClick={() => runBoardSummarize({ force: false })}
                >
                  <Sparkles className="h-4 w-4 mr-1.5" />
                  {boardSummarizing ? "Summarizing…" : "Summarize board"}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={boardSummarizing}
                  onClick={() => runBoardSummarize({ force: true })}
                >
                  <Sparkles className="h-4 w-4 mr-1.5" />
                  {boardSummarizing ? "Updating…" : "Update summary"}
                </Button>
              )}
            </div>
            <div className="mb-4">
              <SprintScrubber
                sprints={sprints}
                selectedIndex={sprintIndex}
                onSelectIndex={handleSprintIndexChange}
                filterMode={sprintFilterMode}
                onFilterModeChange={handleSprintFilterChange}
                onStartSprint={handleStartSprint}
                onRenameSprint={openRenameSprint}
                canManage={canManageLanes}
                starting={startingSprint}
              />
            </div>
            {(boardBriefing || boardSummarizeError) && (
              <div className="mb-4 rounded-lg border border-border bg-muted/40 px-4 py-3 space-y-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    Board summary
                  </h3>
                  {boardSummarizeMeta && !boardSummarizeError ? (
                    <p className="text-xs text-muted-foreground">
                      {boardSummarizeMeta.fromCache ||
                      boardSummarizeMeta.reason === "unchanged"
                        ? "Cached"
                        : "Just generated"}
                      {boardSummarizeMeta.generatedAt
                        ? ` · ${new Date(
                            boardSummarizeMeta.generatedAt
                          ).toLocaleString()}`
                        : ""}
                    </p>
                  ) : null}
                </div>
                {boardSummarizeError ? (
                  <p className="text-sm text-destructive" role="alert">
                    {boardSummarizeError}
                  </p>
                ) : (
                  <CatchUpBriefing
                    briefing={boardBriefing}
                    returnPath={returnPathForBoard}
                    orgId={orgId}
                    showThreadLinks
                    showPlaceLabel={false}
                  />
                )}
              </div>
            )}
            <KanbanBoard
              posts={posts}
              allPosts={allKanbanPosts}
              loading={loading}
              onCardMove={
                userMembershipStatus === "member" ? handleCardMove : undefined
              }
              onCreateCard={handleCreateCard}
              onCardClick={handleCardClick}
              onCardEdit={
                userMembershipStatus === "member" ? handleCardEdit : undefined
              }
              canCreateCard={canCreateCard}
              teamMemberIds={teamMemberIds}
              lanes={kanbanLanes}
              highlightedCardId={highlightedCardId}
              inspectCardId={inspectTarget?.id ?? null}
              inspectNonce={inspectTarget?.token ?? 0}
              onInspectDismissed={dismissCardInspect}
              stubs={handoffStubs}
              orgId={orgId}
              teamId={teamId}
              onStubClick={handleStubClick}
              onDismissStub={handleDismissStub}
              completedWorkLane={completedWorkLaneName}
              defaultChannel={defaultChannel}
              onViewTeamChannel={handleViewTeamChannel}
            />
          </TabsContent>
          )}

          {teamKanbanEnabled && (
          <TabsContent value="roadmap" className="mt-4 min-w-0 max-w-full">
            <TeamRoadmap
              posts={roadmapPosts}
              stubs={roadmapStubs}
              sprints={sprints}
              completedWorkLane={completedWorkLaneName}
              onCardClick={handleCardClick}
              onStubClick={handleStubClick}
              onDismissStub={handleDismissStub}
              orgId={orgId}
              teamId={teamId}
            />
          </TabsContent>
          )}

          <TabsContent value="channels" className="mt-4">
            <TeamChannelsPanel
              orgId={orgId}
              teamId={teamId}
              teamVisibility={team.visibility}
              isTeamAdmin={isTeamAdminEffective}
            />
          </TabsContent>

          {(userMembershipStatus === "member" ||
            isOrgOwner ||
            userMembershipStatus === "following" ||
            team.visibility === "organization") && (
            <TabsContent value="files" className="mt-4">
              <TeamFilesPanel
                orgId={orgId}
                teamId={teamId}
                accessToken={accessToken}
                currentUserId={currentUser?.user_id}
                isTeamAdmin={isTeamAdminEffective}
                canManageSettings={canManageOrgSettings}
              />
            </TabsContent>
          )}

          {/* Members Tab */}
          <TabsContent value="members" className="mt-4">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {(userMembershipStatus === "member" || isOrgOwner) && (
                <Button onClick={() => setShowInviteDialog(true)} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Invite User
                </Button>
              )}
              {members.length > 0 && (
                <div className="max-w-md flex-1 sm:ml-auto">
                  <Label htmlFor="team-member-search" className="sr-only">
                    Search members
                  </Label>
                  <Input
                    id="team-member-search"
                    type="search"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Search by name, email, title, or role…"
                  />
                </div>
              )}
            </div>
            {members.length === 0 ? (
              <p className="text-muted-foreground">
                No members yet.
              </p>
            ) : filteredTeamMembers.length === 0 ? (
              <p className="text-muted-foreground">
                No members match “{memberSearch.trim()}”.
              </p>
            ) : (
              <div className="space-y-3">
                {filteredTeamMembers.map((member) => {
                  const isBot = Boolean(member.is_bot || member.role === "bot");
                  const roleLabel = isBot
                    ? "Bot"
                    : member.role === "admin"
                      ? "Admin"
                      : "Member";
                  return (
                    <Card key={member.user_id}>
                      <CardContent className="flex items-center gap-4 p-4">
                        <MemberProfileHover user={member} orgId={orgId}>
                          <OrgMemberAvatar user={member} />
                        </MemberProfileHover>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">
                            <MemberProfileHover user={member} orgId={orgId}>
                              <span className="hover:underline">
                                {getFullName(member) || "Unknown"}
                              </span>
                            </MemberProfileHover>
                          </p>
                          {member.org_email && (
                            <p className="text-sm text-muted-foreground truncate">
                              {member.org_email}
                            </p>
                          )}
                          {member.title && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {member.title}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-1 mt-2">
                            <Badge
                              variant={
                                member.role === "admin" ? "default" : "outline"
                              }
                            >
                              {roleLabel}
                            </Badge>
                          </div>
                        </div>
                        {isBot &&
                        canIntegrations &&
                        member.installation_id ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Manage Circatron ${
                              getFullName(member) || "bot"
                            }`}
                            onClick={() =>
                              navigate(
                                `/orgs/${encodeURIComponent(
                                  orgId
                                )}/settings?section=connectors&installation=${encodeURIComponent(
                                  String(member.installation_id)
                                )}`
                              )
                            }
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {isTeamAdminEffective && !isBot ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit permissions for ${getFullName(member) || "member"}`}
                            onClick={() => openMemberPermissions(member)}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* About Tab */}
          <TabsContent value="about" className="mt-4">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground whitespace-pre-wrap">
                    {team.description || "No description provided."}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Members</p>
                      <p className="text-2xl font-bold">{members.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Posts</p>
                      <p className="text-2xl font-bold">{posts.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Team Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Created</p>
                    <p>{new Date(team.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Visibility</p>
                    <div className="flex items-center gap-2">
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex cursor-help">
                              <Badge variant={team.visibility === "organization" ? "default" : "destructive"}>
                                {team.visibility === "organization" ? "Organization-wide" : "Restricted"}
                              </Badge>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-sm leading-snug">
                            {team.visibility === "organization"
                              ? TEAM_ORGANIZATION_TOOLTIP
                              : TEAM_RESTRICTED_TOOLTIP}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  {team.tags && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Tags</p>
                      <div className="flex flex-wrap gap-2">
                        {team.tags.split(',').map((tag, idx) => (
                          <Badge key={idx} variant="secondary">{tag.trim()}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {orgId && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Organization</p>
                      <Button
                        variant="link"
                        className="p-0 h-auto"
                        onClick={() => navigate(`/orgs/${orgId}`)}
                      >
                        View Organization
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

            </div>
          </TabsContent>

        </Tabs>
      </div>

      {/* Kanban Lane Manager Dialog */}
      {canManageLanes && (
          <KanbanLaneManager
            isOpen={showLaneManager}
            onClose={() => setShowLaneManager(false)}
            lanes={Array.isArray(kanbanLanes) ? kanbanLanes : []}
            teamId={teamId}
            orgId={orgId}
            accessToken={accessToken}
            posts={posts}
            intakeLaneId={team?.intake_lane_id ?? null}
            onLanesUpdated={async () => {
              // Refresh lanes after update
              try {
                const lanesData = await makeAuthenticatedRequest(fetchKanbanLanes);
                console.log("Refreshed lanes data:", lanesData);
                
                // Handle different response structures
                let lanesArray = lanesData;
                if (lanesData && typeof lanesData === 'object' && !Array.isArray(lanesData)) {
                  lanesArray = lanesData.data || lanesData.lanes || lanesData;
                }
                
                // Ensure lanes are filtered to only include lanes for this team
                const filteredLanes = Array.isArray(lanesArray) 
                  ? lanesArray.filter(lane => !lane.team_id || lane.team_id === parseInt(teamId))
                  : [];
                
                console.log("Refreshed filtered lanes:", filteredLanes);
                setKanbanLanes(filteredLanes);

                // Deleting intake / completed-work lane clears those pointers on the team
                try {
                  const teamData = await makeAuthenticatedRequest(fetchTeamDetails);
                  setTeam(teamData);
                } catch (teamErr) {
                  console.error("Failed to refresh team after lane update:", teamErr);
                }
                
                // Also refresh posts to get updated kanban_status values after lane rename
                try {
                  await refreshTeamPosts();
                  console.log("Refreshed posts after lane update");
                } catch (postsErr) {
                  console.error("Failed to refresh posts after lane update:", postsErr);
                  // Don't throw - lanes were updated successfully
                }
              } catch (err) {
                console.error("Failed to refresh lanes:", err);
                // Keep existing lanes on error
              }
            }}
          />
      )}

      {/* Leave Team Confirmation Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to leave "{team?.name}"? You will no longer be a member of this team and will lose access to team-specific content.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLeaveDialog(false)}
              disabled={isLeaving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLeaveTeam}
              disabled={isLeaving}
            >
              {isLeaving ? "Leaving..." : "Leave Team"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unfollow Team Confirmation Dialog */}
      <Dialog open={showUnfollowDialog} onOpenChange={setShowUnfollowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unfollow Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to unfollow "{team?.name}"? You will no longer receive updates from this team.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUnfollowDialog(false)}
              disabled={isUnfollowing}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleUnfollowTeam}
              disabled={isUnfollowing}
            >
              {isUnfollowing ? "Unfollowing..." : "Unfollow"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove member confirmation */}
      <Dialog
        open={showRemoveMemberDialog}
        onOpenChange={(open) => {
          if (isRemovingMember) return;
          setShowRemoveMemberDialog(open);
          if (!open) setRemoveMemberTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from team</DialogTitle>
            <DialogDescription>
              Remove {removeMemberTarget?.name || "this member"} from{" "}
              {team?.name ? `"${team.name}"` : "this team"}? They will lose access
              to team channels and content until invited again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoveMemberDialog(false);
                setRemoveMemberTarget(null);
              }}
              disabled={isRemovingMember}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveMember}
              disabled={isRemovingMember}
            >
              {isRemovingMember ? "Removing..." : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename sprint */}
      <Dialog
        open={showRenameSprintDialog}
        onOpenChange={(open) => {
          if (renamingSprint) return;
          setShowRenameSprintDialog(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename sprint</DialogTitle>
            <DialogDescription>
              Change the display name for this sprint. Dates stay the same.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="rename-sprint-name">Name</Label>
            <Input
              id="rename-sprint-name"
              value={renameSprintName}
              onChange={(e) => setRenameSprintName(e.target.value)}
              disabled={renamingSprint}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRenameSprintDialog(false)}
              disabled={renamingSprint}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameSprint}
              disabled={renamingSprint || !renameSprintName.trim()}
            >
              {renamingSprint ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showSprintCadenceDialog}
        onOpenChange={(open) => {
          if (startingSprint) return;
          setShowSprintCadenceDialog(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sprint cadence</DialogTitle>
            <DialogDescription>
              Set how long sprints run and which weekday they start. If this
              sprint already started, pick that earlier weekday.
            </DialogDescription>
          </DialogHeader>
          <SprintCadenceFields
            idPrefix="start-sprint-cadence"
            lengthDays={sprintCadenceDraft.sprint_length_days}
            startWeekday={sprintCadenceDraft.sprint_start_weekday}
            startsAt={sprintCadenceDraft.starts_at}
            disabled={startingSprint}
            onChange={({ lengthDays, startWeekday }) =>
              setSprintCadenceDraft((prev) => ({
                ...prev,
                sprint_length_days: lengthDays,
                sprint_start_weekday: startWeekday,
              }))
            }
            onStartsAtChange={(startsAt) =>
              setSprintCadenceDraft((prev) => ({
                ...prev,
                starts_at: startsAt,
              }))
            }
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSprintCadenceDialog(false)}
              disabled={startingSprint}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAndStartSprint}
              disabled={
                startingSprint ||
                !isAllowedFirstSprintStart(sprintCadenceDraft.starts_at, {
                  lengthDays: sprintCadenceDraft.sprint_length_days,
                  startWeekday: sprintCadenceDraft.sprint_start_weekday,
                })
              }
            >
              {startingSprint ? "Starting…" : "Save and start"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Kanban Card Dialog */}
      <Dialog
        open={showCreateCardDialog}
        onOpenChange={(open) => {
          setShowCreateCardDialog(open);
          if (!open) setCardPendingFiles([]);
        }}
      >
        {showCreateCardDialog ? (
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New work</DialogTitle>
            <DialogDescription>
              {userMembershipStatus !== "member"
                ? "Submit a card to this team’s intake lane"
                : "Create an epic, story, or task on this board"}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const isNonMemberCreator = userMembershipStatus !== "member";
            const intakeLane = kanbanLanes.find(
              (l) => l.lane_id === (team?.intake_lane_id ?? null)
            );
            const nonMemberMissingIntake = isNonMemberCreator && !intakeLane;
            return (
          <div className="space-y-4 py-4">
            {nonMemberMissingIntake && (
              <Alert variant="destructive">
                <AlertDescription>
                  A team admin has not configured an intake lane yet. Cards cannot
                  be submitted until intake is set.
                </AlertDescription>
              </Alert>
            )}
            {isNonMemberCreator && intakeLane && (
              <p className="text-sm text-muted-foreground">
                Goes to <span className="font-medium text-foreground">{intakeLane.name}</span>
                {" "}(intake)
              </p>
            )}
            <div>
              <Label htmlFor="card-title">Title *</Label>
              <Input
                id="card-title"
                value={cardFormData.title}
                onChange={(e) => setCardFormData({ ...cardFormData, title: e.target.value })}
                placeholder="Enter card title"
                disabled={nonMemberMissingIntake}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="card-content">Content *</Label>
              <RichMarkdownEditor
                id="card-content"
                value={cardFormData.content}
                onChange={(next) =>
                  setCardFormData((prev) => ({ ...prev, content: next }))
                }
                onSubmit={handleSubmitCreateCard}
                submitMode="mod-enter"
                variant="compact"
                minRows={4}
                placeholder="Enter card description"
                disabled={nonMemberMissingIntake || isCreatingCard}
              />
            </div>
            <div className="space-y-2">
              <Label>Attachments</Label>
              <MediaAttachBar
                mode="files"
                pendingFiles={cardPendingFiles}
                onFilesChange={setCardPendingFiles}
                disabled={nonMemberMissingIntake || isCreatingCard}
              />
            </div>
            {!isNonMemberCreator && (
            <>
            <div>
              <Label htmlFor="card-work-kind">Type</Label>
              <select
                id="card-work-kind"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={cardFormData.work_kind || "task"}
                onChange={(e) => changeCardWorkKind(e.target.value)}
              >
                <option value="task">Task</option>
                <option value="story">Story</option>
                <option value="epic">Epic</option>
              </select>
            </div>
            {cardFormData.work_kind !== "epic" && (
              <div>
                <Label htmlFor="card-parent">Parent</Label>
                <select
                  id="card-parent"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={cardFormData.parent_id || ""}
                  onChange={(e) =>
                    setCardFormData({ ...cardFormData, parent_id: e.target.value })
                  }
                >
                  <option value="">None</option>
                  {parentPickerOptions.map((p) => {
                    const id = p.post_id || p.postId;
                    return (
                      <option key={id} value={String(id)}>
                        {workKindOf(p) === "epic" ? "Epic" : "Story"}: {p.title}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            {cardFormData.work_kind !== "epic" && (
              <div>
                <Label htmlFor="card-points">Story points</Label>
                <select
                  id="card-points"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={cardFormData.story_points || ""}
                  onChange={(e) =>
                    setCardFormData({
                      ...cardFormData,
                      story_points: e.target.value,
                    })
                  }
                >
                  <option value="">None</option>
                  {FIBONACCI_POINTS.map((n) => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label htmlFor="card-status">Status</Label>
              <select
                id="card-status"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={cardFormData.kanban_status}
                onChange={(e) => setCardFormData({ ...cardFormData, kanban_status: e.target.value })}
              >
                {kanbanLanes.length > 0 ? (
                  [...kanbanLanes]
                    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                    .map((lane) => (
                      <option key={lane.lane_id} value={lane.name}>
                        {lane.name}
                      </option>
                    ))
                ) : (
                  <>
                    <option value="Backlog">Backlog</option>
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="In Review">In Review</option>
                    <option value="Done">Done</option>
                  </>
                )}
              </select>
            </div>
            </>
            )}
            {!isNonMemberCreator && (
            <>
            <div>
              <CardAssigneesPicker
                idPrefix="create-card-assignee"
                teamMembers={teamMembers}
                value={cardFormData.assignee_ids || []}
                onChange={(assignee_ids) =>
                  setCardFormData({ ...cardFormData, assignee_ids })
                }
              />
            </div>
            <div>
              <Label htmlFor="card-starts-at">Start date</Label>
              <Input
                id="card-starts-at"
                type="date"
                value={cardFormData.starts_at || ""}
                onChange={(e) =>
                  setCardFormData({ ...cardFormData, starts_at: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground mt-1">
                With a due date, both appear as a bar on the team Roadmap.
              </p>
            </div>
            <div>
              <Label htmlFor="card-due-date">Due Date</Label>
              <Input
                id="card-due-date"
                type="date"
                value={cardFormData.due_date}
                onChange={(e) => setCardFormData({ ...cardFormData, due_date: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="card-priority">Priority</Label>
              <select
                id="card-priority"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={cardFormData.priority}
                onChange={(e) => setCardFormData({ ...cardFormData, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            </>
            )}
          </div>
            );
          })()}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateCardDialog(false)}
              disabled={isCreatingCard}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitCreateCard}
              disabled={
                isCreatingCard ||
                (userMembershipStatus !== "member" &&
                  !kanbanLanes.some(
                    (l) => l.lane_id === (team?.intake_lane_id ?? null)
                  ))
              }
            >
              {isCreatingCard ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
        ) : null}
      </Dialog>

      <CardEditDialog
        open={showCardDetailDialog}
        onOpenChange={(open) => {
          setShowCardDetailDialog(open);
          if (!open) {
            setSelectedCard(null);
          }
        }}
        card={selectedCard}
        onCardChange={setSelectedCard}
        orgId={orgId}
        teamId={teamId}
        accessToken={accessToken}
        kanbanLanes={kanbanLanes}
        sprints={sprints}
        teamMembers={teamMembers}
        parentCandidates={allKanbanPosts}
        canManageOrgSettings={canManageOrgSettings}
        onAddChild={handleAddChildFromEdit}
        showHandoff={userMembershipStatus === "member"}
        onHandoff={() => {
          setShowCardDetailDialog(false);
          setShowHandoffDialog(true);
        }}
        onCancelPendingHandoff={handleCancelPendingHandoff}
        cancellingHandoff={isCancellingHandoff}
        onDelete={canDeleteCards ? handleSoftDeleteCard : undefined}
        onSaved={async () => {
          setShowCardDetailDialog(false);
          setSelectedCard(null);
          setHighlightedCardId(null);
          await refreshTeamPosts();
          await loadRoadmapData();
        }}
      />

      <HandoffDialog
        open={showHandoffDialog}
        onClose={() => {
          setShowHandoffDialog(false);
          if (!showCardDetailDialog) {
            setSelectedCard(null);
            setHighlightedCardId(null);
          }
        }}
        post={selectedCard}
        orgId={orgId}
        currentTeamId={teamId ? parseInt(teamId, 10) : undefined}
        accessToken={accessToken}
        onSuccess={async () => {
          const postsList = await refreshTeamPosts();
          if (!selectedCard) return;
          const cardId = selectedCard.post_id || selectedCard.postId;
          const updated = postsList.find(
            (p) => (p.post_id || p.postId) === cardId
          );
          if (updated) setSelectedCard(updated);
        }}
      />

      <InboundHandoffsPanel
        open={showInboundHandoffs}
        onClose={() => setShowInboundHandoffs(false)}
        teamId={teamId ? parseInt(teamId, 10) : undefined}
        orgId={orgId}
        accessToken={accessToken}
        lanes={kanbanLanes}
        onChanged={async () => {
          await refreshTeamPosts();
          await refreshTeamHomeExtras();
        }}
      />

      {/* Invite User Dialog */}
      <InviteUserDialog
        isOpen={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        orgId={orgId}
        teamId={teamId ? parseInt(teamId) : undefined}
        onSuccess={() => {
          // Refresh members after invite
          const refreshMembers = async () => {
            try {
              const membersData = await makeAuthenticatedRequest(fetchTeamMembers);
              // Map to format expected by members display (flatten user object)
              const mappedMembers = membersData.map(item => ({
                ...item.user,
                role: item.role,
                overrides: item.overrides || [],
                title: item.title,
                org_email: item.org_email || null,
                member_avatar_url:
                  item.member_avatar_url || item.user?.member_avatar_url || null,
              }));
              setMembers(mappedMembers);
              setTeamMembers(mappedMembers);
              
              // Also fetch org members for post title lookup
              await fetchOrgMembers();
            } catch (err) {
              console.error("Failed to refresh members:", err);
            }
          };
          refreshMembers();
        }}
      />

      {/* Member permissions dialog */}
      <Dialog
        open={!!memberPermissionsTarget}
        onOpenChange={(open) => {
          if (!open) closeMemberPermissions();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Member access</DialogTitle>
            <DialogDescription>
              {memberPermissionsTarget
                ? `Set role and optional access for ${
                    getFullName(memberPermissionsTarget) || "this member"
                  }.`
                : "Set role and optional access."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label>Role</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    value: "member",
                    label: "Member",
                    hint: "Post, comment, invite others, and work on any board card",
                  },
                  {
                    value: "admin",
                    label: "Admin",
                    hint: "Everything members can do, plus settings, people, and board structure",
                  },
                ].map((option) => {
                  const selected = memberPermissionsForm.role === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={isSavingMemberPermissions}
                      onClick={() =>
                        setMemberPermissionsForm((prev) => ({
                          ...prev,
                          role: option.value,
                        }))
                      }
                      className={cn(
                        "rounded-md border px-3 py-3 text-left transition-colors",
                        selected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      )}
                    >
                      <p className="text-sm font-medium">{option.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {option.hint}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {memberPermissionsForm.role === "member" && (
              <div className="space-y-2">
                <Label>Additional access</Label>
                <p className="text-xs text-muted-foreground">
                  Optional grants beyond the default member role.
                </p>
                <div className="space-y-2">
                  {[
                    {
                      key: "kanban.manage_lanes",
                      label: "Manage lanes",
                      description: "Add, rename, reorder, and delete board lanes",
                    },
                    {
                      key: "cards.delete",
                      label: "Delete cards",
                      description: "Soft-delete cards from the board",
                    },
                  ].map((grant) => {
                    const enabled = memberPermissionsForm.overrides.includes(
                      grant.key
                    );
                    return (
                      <button
                        key={grant.key}
                        type="button"
                        disabled={isSavingMemberPermissions}
                        onClick={() => toggleMemberOverride(grant.key)}
                        className={cn(
                          "flex w-full items-start justify-between gap-3 rounded-md border px-3 py-3 text-left transition-colors",
                          enabled
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/50"
                        )}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{grant.label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {grant.description}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "mt-0.5 shrink-0 text-xs font-medium",
                            enabled ? "text-primary" : "text-muted-foreground"
                          )}
                        >
                          {enabled ? "On" : "Off"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            {memberPermissionsTarget &&
              memberPermissionsTarget.user_id !== currentUser?.user_id && (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={isSavingMemberPermissions || isRemovingMember}
                  onClick={() => {
                    setRemoveMemberTarget({
                      userId: memberPermissionsTarget.user_id,
                      name: getFullName(memberPermissionsTarget) || "this member",
                    });
                    setShowRemoveMemberDialog(true);
                  }}
                >
                  Remove from team
                </Button>
              )}
            <div className="flex gap-2 sm:ml-auto">
              <Button
                type="button"
                variant="outline"
                onClick={closeMemberPermissions}
                disabled={isSavingMemberPermissions}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveMemberPermissions}
                disabled={isSavingMemberPermissions}
              >
                {isSavingMemberPermissions ? "Saving..." : "Save"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamDetail;

